import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebase';

const SUBSCRIPTIONS_COLLECTION = 'subscriptions';

/**
 * Subscribe to all subscriptions with real-time updates.
 * Groups by student/account (Email/User) so each subscribed student
 * represents an All-Access course subscription.
 */
export const subscribeToSubscriptions = (callback) => {
  let usersMap = {};
  let subjectsDocs = [];

  const checkAndEmit = () => {
    const userSubsMap = {};
    const now = new Date();

    // 1. Group all subject documents by userId
    subjectsDocs.forEach((docSnap) => {
      const pathParts = docSnap.ref.path.split('/');
      if (pathParts[0] !== 'subscriptions') return;
      
      const parentDocRef = docSnap.ref.parent?.parent;
      if (!parentDocRef) return;
      
      const userId = parentDocRef.id;
      const data = docSnap.data();
      const subjectId = docSnap.id;
      const isClassPackage = subjectId === '_class_package';

      if (!userSubsMap[userId]) {
        userSubsMap[userId] = {
          userId,
          classPackageDoc: null,
          subjectDocs: [],
          hasActive: false,
          maxExpiryDate: null,
          minStartDate: null,
          plans: new Set(),
          amounts: [],
        };
      }

      const userEntry = userSubsMap[userId];
      if (isClassPackage) {
        userEntry.classPackageDoc = data;
      } else {
        userEntry.subjectDocs.push({ id: subjectId, ...data });
      }

      // Check expiry date
      const rawExpiry = isClassPackage ? data.classPackageExpiresAt : data.expiresAt;
      let expObj = null;
      if (rawExpiry) {
        expObj = rawExpiry.toDate ? rawExpiry.toDate() : new Date(rawExpiry);
      }
      if (expObj && !isNaN(expObj.getTime())) {
        if (!userEntry.maxExpiryDate || expObj > userEntry.maxExpiryDate) {
          userEntry.maxExpiryDate = expObj;
        }
      }

      // Check start date
      const rawStart = isClassPackage ? (data.lastRenewal || data.enrolledAt) : data.enrolledAt;
      let startObj = null;
      if (rawStart) {
        startObj = rawStart.toDate ? rawStart.toDate() : new Date(rawStart);
      }
      if (startObj && !isNaN(startObj.getTime())) {
        if (!userEntry.minStartDate || startObj < userEntry.minStartDate) {
          userEntry.minStartDate = startObj;
        }
      }

      // Check active status
      const isActive = isClassPackage
        ? (data.classPackageActive === true && (!expObj || expObj > now))
        : ((data.status === 'active' || data.grantedByAdmin) && (!expObj || expObj > now));

      if (isActive) {
        userEntry.hasActive = true;
      }

      if (data.plan) userEntry.plans.add(data.plan);
      if (data.amount) userEntry.amounts.push(data.amount);
    });

    // 2. Also check usersMap for users who have isPremium or scholarship
    Object.keys(usersMap).forEach((uId) => {
      const u = usersMap[uId];
      if (u && (u.isPremium === true || u.scholarship?.active === true)) {
        if (!userSubsMap[uId]) {
          const exp = u.scholarship?.expiresAt;
          const expObj = exp ? (exp.toDate ? exp.toDate() : new Date(exp)) : null;
          userSubsMap[uId] = {
            userId: uId,
            classPackageDoc: { classPackageActive: true },
            subjectDocs: [],
            hasActive: !expObj || expObj > now,
            maxExpiryDate: expObj || new Date(Date.now() + 365 * 86400000),
            minStartDate: u.createdAt?.toDate ? u.createdAt.toDate() : new Date(),
            plans: new Set(['All-Access Scholarship']),
            amounts: ['₦0 (Scholarship)'],
          };
        } else {
          userSubsMap[uId].hasActive = true;
        }
      }
    });

    // 3. Build one clean account-level subscription item per student
    const subscriptions = [];

    Object.values(userSubsMap).forEach((entry) => {
      const userId = entry.userId;
      const user = usersMap[userId] || {};
      const status = entry.hasActive ? 'active' : 'expired';
      const expiryDate = entry.maxExpiryDate || new Date(Date.now() + 30 * 86400000);
      const startDate = entry.minStartDate || new Date();
      
      const classLevel = (user.classLevel || 'ss1').toUpperCase();
      const planName = entry.plans.size > 0 
        ? Array.from(entry.plans)[0] 
        : (entry.classPackageDoc ? 'All-Access Class Package' : 'All Courses Plan');

      const amount = entry.amounts.length > 0 ? entry.amounts[0] : '₦10,000';

      subscriptions.push({
        id: `sub_${userId}`,
        userId: userId,
        subjectId: '_class_package',
        student: user.fullName || 'Student',
        email: user.email || 'N/A',
        class: classLevel,
        subject: `All Courses (${classLevel})`,
        plan: planName,
        amount: amount,
        startDate: startDate,
        expiryDate: expiryDate,
        status: status,
        createdAt: startDate,
        totalSubjectsCount: entry.subjectDocs.length,
      });
    });

    // Sort by latest expiry/createdAt
    subscriptions.sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt ? new Date(a.createdAt) : new Date(0));
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt ? new Date(b.createdAt) : new Date(0));
      return dateB - dateA;
    });

    callback(subscriptions);
  };

  // Listen to users
  const unsubscribeUsers = onSnapshot(collection(db, 'users'), (snapshot) => {
    usersMap = {};
    snapshot.docs.forEach((docSnap) => {
      usersMap[docSnap.id] = docSnap.data();
    });
    checkAndEmit();
  }, (err) => {
    console.warn('Subscription service: users listener error', err);
    checkAndEmit();
  });

  // Listen to collectionGroup subjects
  const unsubscribeSubjects = onSnapshot(collectionGroup(db, 'subjects'), (snapshot) => {
    subjectsDocs = snapshot.docs;
    checkAndEmit();
  }, (err) => {
    console.warn('Subscription service: subjects collectionGroup listener error', err);
    checkAndEmit();
  });

  // Return function to unsubscribe both
  return () => {
    unsubscribeUsers();
    unsubscribeSubjects();
  };
};

/**
 * Add a new subscription
 */
export const addSubscription = async (subscriptionData) => {
  try {
    const docRef = await addDoc(collection(db, SUBSCRIPTIONS_COLLECTION), {
      ...subscriptionData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding subscription:', error);
    throw error;
  }
};

/**
 * Update an existing subscription
 */
export const updateSubscription = async (subscriptionId, updates) => {
  try {
    const subscriptionRef = doc(db, SUBSCRIPTIONS_COLLECTION, subscriptionId);
    await updateDoc(subscriptionRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating subscription:', error);
    throw error;
  }
};

/**
 * Delete a student's subscription (all subjects & account level)
 */
export const deleteSubscription = async (userId) => {
  try {
    const subjectsRef = collection(db, 'subscriptions', userId, 'subjects');
    const snapshot = await getDocs(subjectsRef);
    const batch = writeBatch(db);
    
    snapshot.forEach(docSnap => {
      batch.delete(docSnap.ref);
    });

    batch.update(doc(db, 'users', userId), {
      isPremium: false,
      scholarship: null,
      updatedAt: serverTimestamp(),
    });

    await batch.commit();
    return true;
  } catch (error) {
    console.error('Error deleting subscription:', error);
    throw error;
  }
};

/**
 * Extend a student's all-access subscription expiry date
 */
export const extendSubscription = async (userId, subjectId, days) => {
  try {
    const subjectsRef = collection(db, 'subscriptions', userId, 'subjects');
    const snapshot = await getDocs(subjectsRef);
    const batch = writeBatch(db);
    const now = new Date();

    let currentExpiry = now;
    snapshot.forEach(docSnap => {
      const data = docSnap.data();
      const rawExp = data.classPackageExpiresAt || data.expiresAt;
      if (rawExp) {
        const d = rawExp.toDate ? rawExp.toDate() : new Date(rawExp);
        if (d > currentExpiry) currentExpiry = d;
      }
    });

    const newExpiry = new Date(currentExpiry.getTime() + days * 24 * 60 * 60 * 1000);

    const classPkgRef = doc(db, 'subscriptions', userId, 'subjects', '_class_package');
    batch.set(classPkgRef, {
      classPackageActive: true,
      classPackageExpiresAt: newExpiry,
      updatedAt: serverTimestamp(),
    }, { merge: true });

    snapshot.forEach(docSnap => {
      if (docSnap.id !== '_class_package') {
        batch.update(docSnap.ref, {
          expiresAt: newExpiry,
          status: 'active',
          updatedAt: serverTimestamp(),
        });
      }
    });

    batch.update(doc(db, 'users', userId), {
      isPremium: true,
      updatedAt: serverTimestamp(),
    });

    await batch.commit();
    return true;
  } catch (error) {
    console.error('Error extending subscription:', error);
    throw error;
  }
};

/**
 * Renew an expired or expiring all-access subscription
 */
export const renewSubscription = async (userId, subjectId, plan, amount, days) => {
  try {
    const newStart = new Date();
    const newExpiry = new Date(newStart.getTime() + days * 24 * 60 * 60 * 1000);
    const formattedAmount = typeof amount === 'string' && amount.startsWith('₦')
      ? amount
      : `₦${Number(amount || 0).toLocaleString('en-NG')}`;

    const subjectsRef = collection(db, 'subscriptions', userId, 'subjects');
    const snapshot = await getDocs(subjectsRef);
    const batch = writeBatch(db);

    const classPkgRef = doc(db, 'subscriptions', userId, 'subjects', '_class_package');
    batch.set(classPkgRef, {
      classPackageActive: true,
      classPackageExpiresAt: newExpiry,
      lastRenewal: newStart,
      tier: 'class',
      plan: plan || 'All-Access Plan',
      amount: formattedAmount,
      updatedAt: serverTimestamp(),
    }, { merge: true });

    snapshot.forEach(docSnap => {
      if (docSnap.id !== '_class_package') {
        batch.update(docSnap.ref, {
          status: 'active',
          enrolledAt: newStart,
          expiresAt: newExpiry,
          plan: plan || 'All-Access Plan',
          amount: formattedAmount,
          updatedAt: serverTimestamp(),
        });
      }
    });

    batch.update(doc(db, 'users', userId), {
      isPremium: true,
      updatedAt: serverTimestamp(),
    });

    await batch.commit();
    return true;
  } catch (error) {
    console.error('Error renewing subscription:', error);
    throw error;
  }
};

/**
 * Calculate subscription statistics (account-level)
 */
export const calculateSubscriptionStats = (subscriptions) => {
  const now = new Date();
  
  const active = subscriptions.filter(s => s.status === 'active').length;
  const expired = subscriptions.filter(s => s.status === 'expired').length;
  const expiring = subscriptions.filter(s => {
    if (s.status !== 'active') return false;
    const expiryDate = s.expiryDate?.toDate?.() || new Date(s.expiryDate);
    const daysUntilExpiry = Math.floor((expiryDate - now) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 7 && daysUntilExpiry >= 0;
  }).length;
  
  const cancelled = subscriptions.filter(s => s.status === 'cancelled').length;
  
  // Calculate total revenue from active/expired accounts
  const totalRevenue = subscriptions
    .filter(s => s.status === 'active' || s.status === 'expired')
    .reduce((sum, s) => {
      const amount = parseFloat(s.amount?.replace(/[^0-9.-]+/g, '') || 0);
      return sum + amount;
    }, 0);
  
  return {
    total: subscriptions.length,
    active,
    expired,
    expiring,
    cancelled,
    totalRevenue,
  };
};

export default {
  subscribeToSubscriptions,
  addSubscription,
  updateSubscription,
  deleteSubscription,
  extendSubscription,
  renewSubscription,
  calculateSubscriptionStats,
};
